/*
 * Product · Also like (sections/pdp-also-like.liquid)
 * - Carousel: endless loop like the live Replo carousel (copies of the cards
 *   sit on both sides of the real ones and the track jumps back without
 *   animation after crossing them). One card per arrow click or swipe, 250ms
 *   ease, and it advances by itself every data-autoplay seconds (paused while
 *   hovered or focused, off with prefers-reduced-motion). Without JS the
 *   cards scroll sideways with scroll-snap.
 * - Add to cart: posts the card's form to /cart/add.js, then opens the cart
 *   drawer (Essential Cart, else the theme drawer #cart-modal, else /cart),
 *   the same order as assets/home-bestseller.js. If fetch fails the form
 *   posts normally.
 */
(() => {
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Carousel --------------------------------------------------------------- */

  function initCarousel(root) {
    if (root.dataset.palReady) return;
    root.dataset.palReady = 'true';

    const viewport = root.querySelector('[data-pal-viewport]');
    const track = root.querySelector('[data-pal-track]');
    const prev = root.querySelector('[data-pal-prev]');
    const next = root.querySelector('[data-pal-next]');
    const status = root.querySelector('[data-pal-status]');
    const dots = Array.from((root.closest('.pdp-also-like') || root).querySelectorAll('[data-pal-dot]'));
    if (!viewport || !track) return;

    const originals = Array.from(track.children).filter((el) => el.hasAttribute('data-pal-slide'));
    const count = originals.length;
    if (count < 2) {
      if (prev) prev.hidden = true;
      if (next) next.hidden = true;
      return;
    }

    const makeClone = (slide) => {
      const clone = slide.cloneNode(true);
      clone.removeAttribute('data-pal-slide');
      clone.removeAttribute('data-shopify-editor-block');
      // Copies are often on screen (several cards show at once), so they stay
      // clickable: hidden from screen readers and the tab order, not inert.
      // Their add-to-cart forms work through the section-level submit handler.
      clone.setAttribute('aria-hidden', 'true');
      clone.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
      clone.querySelectorAll('a, button, input, select, textarea').forEach((el) => el.setAttribute('tabindex', '-1'));
      return clone;
    };
    originals.forEach((slide) => track.insertBefore(makeClone(slide), originals[0]));
    originals.forEach((slide) => track.appendChild(makeClone(slide)));

    root.classList.add('is-ready');
    viewport.scrollLeft = 0;

    // Card widths follow the viewport (whole cards per view), so the arrows
    // are lined up with the middle of the card photo by measuring it.
    const placeArrows = () => {
      const media = track.querySelector('.pdp-also-like__image');
      if (!media || !media.offsetHeight) return;
      const top = media.getBoundingClientRect().top - root.getBoundingClientRect().top + media.offsetHeight / 2;
      root.style.setProperty('--al-arrow-top', `${Math.round(top)}px`);
    };
    placeArrows();
    track.querySelectorAll('img').forEach((img) => {
      if (!img.complete) img.addEventListener('load', placeArrows, { once: true });
    });

    let index = count; // first real card
    let dragX = 0;

    const step = () => {
      const slides = track.children;
      // Fractional, since card widths are a share of the viewport.
      return slides[1].getBoundingClientRect().left - slides[0].getBoundingClientRect().left;
    };

    const announce = () => {
      if (!status) return;
      const position = (((index - count) % count) + count) % count;
      status.textContent = `${position + 1} / ${count}`;
    };

    // Dots follow every move, autoplay included (announce() stays quiet then).
    function syncDots() {
      const position = (((index - count) % count) + count) % count;
      dots.forEach((dot, i) => {
        if (i === position) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }

    function apply(instant) {
      track.classList.toggle('is-instant', Boolean(instant) || reduceMotion());
      track.style.transform = `translate3d(${-index * step() + dragX}px, 0, 0)`;
    }

    // Jump back into the real cards (no animation) once a copy is showing.
    function normalize() {
      if (index >= count && index < count * 2) return;
      index = ((((index - count) % count) + count) % count) + count;
      apply(true);
      void track.offsetWidth; // flush so the next move animates from here
    }

    // `to` is counted from an index normalize() has already brought back
    // into the real cards.
    function go(to, { quiet = false } = {}) {
      index = to;
      apply(false);
      syncDots();
      if (!quiet) announce();
      if (reduceMotion()) normalize();
    }

    const move = (delta, options) => {
      normalize();
      go(index + delta, options);
    };

    track.addEventListener('transitionend', (event) => {
      if (event.target === track && event.propertyName === 'transform') normalize();
    });

    if (prev) prev.addEventListener('click', () => { move(-1); restartAutoplay(); });
    if (next) next.addEventListener('click', () => { move(1); restartAutoplay(); });

    // Dots: go the short way round the loop to the chosen card.
    dots.forEach((dot, i) => {
      dot.addEventListener('click', () => {
        normalize();
        let delta = i - (index - count);
        if (delta > count / 2) delta -= count;
        if (delta < -count / 2) delta += count;
        if (delta) go(index + delta);
        restartAutoplay();
      });
    });

    /* Swipe / drag */
    let startX = 0;
    let startY = 0;
    let pointerId = null;
    let dragging = false;
    let suppressClick = false;

    viewport.addEventListener('pointerdown', (event) => {
      if (event.button !== 0) return;
      pointerId = event.pointerId;
      startX = event.clientX;
      startY = event.clientY;
      dragging = false;
      normalize();
    });

    viewport.addEventListener('pointermove', (event) => {
      if (event.pointerId !== pointerId) return;
      const dx = event.clientX - startX;
      const dy = event.clientY - startY;
      if (!dragging) {
        if (Math.abs(dx) < 6 || Math.abs(dx) < Math.abs(dy)) return;
        dragging = true;
        pauseAutoplay();
        try { viewport.setPointerCapture(pointerId); } catch (error) { /* ignore */ }
      }
      dragX = dx;
      apply(true);
    });

    const endDrag = (event) => {
      if (event.pointerId !== pointerId) return;
      pointerId = null;
      if (!dragging) return;
      dragging = false;
      suppressClick = true;
      setTimeout(() => { suppressClick = false; }, 0);
      const dx = dragX;
      dragX = 0;
      const threshold = Math.min(60, step() / 4);
      if (dx <= -threshold) go(index + 1);
      else if (dx >= threshold) go(index - 1);
      else apply(false);
      restartAutoplay();
    };
    viewport.addEventListener('pointerup', endDrag);
    viewport.addEventListener('pointercancel', endDrag);
    viewport.addEventListener('click', (event) => {
      if (suppressClick) {
        event.preventDefault();
        event.stopPropagation();
      }
    }, true);
    viewport.addEventListener('dragstart', (event) => event.preventDefault());

    /* Autoplay */
    const seconds = Number(root.dataset.autoplay) || 0;
    const designMode = Boolean(window.Shopify && window.Shopify.designMode);
    let timer = null;
    let hovered = false;
    let focused = false;

    function pauseAutoplay() {
      clearInterval(timer);
      timer = null;
    }

    function restartAutoplay() {
      pauseAutoplay();
      if (!seconds || designMode || reduceMotion() || hovered || focused || document.hidden) return;
      timer = setInterval(() => move(1, { quiet: true }), seconds * 1000);
    }

    root.addEventListener('mouseenter', () => { hovered = true; pauseAutoplay(); });
    root.addEventListener('mouseleave', () => { hovered = false; restartAutoplay(); });
    root.addEventListener('focusin', () => { focused = true; pauseAutoplay(); });
    root.addEventListener('focusout', (event) => {
      if (root.contains(event.relatedTarget)) return;
      focused = false;
      restartAutoplay();
    });
    document.addEventListener('visibilitychange', restartAutoplay);

    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => { apply(true); placeArrows(); }, 100);
    });

    // Theme editor: show the card whose block is selected.
    root.addEventListener('pal:select', (event) => {
      const at = originals.indexOf(event.detail);
      if (at < 0) return;
      index = count + at;
      apply(true);
      announce();
      syncDots();
    });

    apply(true);
    restartAutoplay();
  }

  /* Cart ------------------------------------------------------------------- */

  async function openCartDrawer(cartUrl) {
    const essential = window.essentialCartApi;
    if (essential && typeof essential.openCart === 'function') {
      if (typeof essential.refreshCart === 'function') await essential.refreshCart();
      essential.openCart();
      return;
    }
    const themeDrawer = document.getElementById('cart-modal');
    const trigger = document.querySelector('[target="#cart-modal"], [data-target="#cart-modal"]');
    if (themeDrawer && trigger) {
      if (typeof themeDrawer.reloadContent === 'function') await themeDrawer.reloadContent();
      trigger.click();
      return;
    }
    window.location.assign(cartUrl || '/cart');
  }

  function initForms(section) {
    if (section.dataset.palFormsReady) return;
    section.dataset.palFormsReady = 'true';

    section.addEventListener('submit', async (event) => {
      const form = event.target.closest('[data-pal-form]');
      if (!form || !window.fetch || !window.FormData) return;
      event.preventDefault();
      const button = form.querySelector('button[type="submit"]');
      const errorBox = form.querySelector('[data-pal-error]');
      if (form.dataset.busy) return;
      form.dataset.busy = 'true';
      if (button) button.setAttribute('aria-busy', 'true');
      if (errorBox) { errorBox.textContent = ''; errorBox.hidden = true; }

      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      let response;
      try {
        response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: new FormData(form),
        });
      } catch (error) {
        form.submit();
        return;
      }

      try {
        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data.description || data.message || 'Could not add to cart.');
        }
        await openCartDrawer(form.dataset.cartUrl);
      } catch (error) {
        if (errorBox) { errorBox.textContent = error.message; errorBox.hidden = false; }
      } finally {
        delete form.dataset.busy;
        if (button) button.removeAttribute('aria-busy');
      }
    });
  }

  /* Boot ------------------------------------------------------------------- */

  function init(scope) {
    scope.querySelectorAll('.pdp-also-like').forEach((section) => {
      initForms(section);
      section.querySelectorAll('[data-pal-carousel]').forEach(initCarousel);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document));
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    const carousel = event.target.closest && event.target.closest('[data-pal-carousel]');
    if (carousel) carousel.dispatchEvent(new CustomEvent('pal:select', { detail: event.target }));
  });
})();
